import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { AxiosError } from 'axios'
import { apiService } from '../../services/api'
import { Test } from '../../types/test'

interface ComplianceRule {
  id: string
  title: string
  category: string
}

interface TestCreateDialogProps {
  open: boolean
  onClose: () => void
  onSuccess: (test: Test) => void
  preselectedComplianceRuleId?: string
}

export const TestCreateDialog: React.FC<TestCreateDialogProps> = ({
  open,
  onClose,
  onSuccess,
  preselectedComplianceRuleId,
}) => {
  const { t } = useTranslation('test')
  const [complianceRules, setComplianceRules] = useState<ComplianceRule[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [formData, setFormData] = useState({
    complianceRuleId: '',
    title: '',
    description: '',
    passingScore: 80,
    timeLimit: 0,
  })

  // 規範一覧を取得
  useEffect(() => {
    if (open) {
      loadComplianceRules()
    }
  }, [open])

  // 事前選択された規範IDを設定
  useEffect(() => {
    if (preselectedComplianceRuleId) {
      setFormData((prev) => ({ ...prev, complianceRuleId: preselectedComplianceRuleId }))
    }
  }, [preselectedComplianceRuleId])

  const loadComplianceRules = async () => {
    setLoading(true)
    try {
      const response = await apiService.get<{ rules: ComplianceRule[] }>('/compliance')
      setComplianceRules(response.rules || [])
    } catch (err) {
      console.error('規範一覧取得エラー:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (field: string, value: string | number) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    setError(null)
  }

  const validateForm = (): boolean => {
    if (!formData.complianceRuleId) {
      setError(t('create.validation.complianceRequired', '学習教材を選択してください'))
      return false
    }
    if (!formData.title.trim()) {
      setError(t('create.validation.titleRequired', 'テストタイトルを入力してください'))
      return false
    }
    return true
  }

  const handleSave = async () => {
    if (!validateForm()) return

    setSaving(true)
    setError(null)

    try {
      const response = await apiService.post<{ message: string; test: Test }>('/tests', {
        complianceRuleId: formData.complianceRuleId,
        title: formData.title.trim(),
        description: formData.description.trim() || null,
        passingScore: formData.passingScore,
        timeLimit: formData.timeLimit,
      })

      onSuccess(response.test)
      handleClose()
    } catch (err) {
      const axiosError = err as AxiosError<{ error: string }>
      const errorMessage =
        axiosError.response?.data?.error || t('create.error.failed', 'テストの作成に失敗しました')
      setError(errorMessage)
    } finally {
      setSaving(false)
    }
  }

  const handleClose = () => {
    setFormData({
      complianceRuleId: preselectedComplianceRuleId || '',
      title: '',
      description: '',
      passingScore: 80,
      timeLimit: 0,
    })
    setError(null)
    onClose()
  }

  // 規範選択時にタイトルを自動設定
  const handleComplianceRuleChange = (ruleId: string) => {
    const selectedRule = complianceRules.find((r) => r.id === ruleId)
    handleChange('complianceRuleId', ruleId)
    if (selectedRule && !formData.title) {
      handleChange('title', `${selectedRule.title} - 理解度テスト`)
    }
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('create.manualTest', 'テストを作成')}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}

          {/* 規範選択 */}
          <FormControl fullWidth required>
            <InputLabel>{t('create.selectCompliance', '学習教材を選択')}</InputLabel>
            <Select
              value={formData.complianceRuleId}
              label={t('create.selectCompliance', '学習教材を選択')}
              onChange={(e) => handleComplianceRuleChange(e.target.value)}
              disabled={loading || !!preselectedComplianceRuleId}
            >
              {loading ? (
                <MenuItem disabled>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  {t('common.loading', '読み込み中...')}
                </MenuItem>
              ) : (
                complianceRules.map((rule) => (
                  <MenuItem key={rule.id} value={rule.id}>
                    {rule.title} ({rule.category})
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>

          {/* テストタイトル */}
          <TextField
            label={t('create.testTitle', 'テストタイトル')}
            value={formData.title}
            onChange={(e) => handleChange('title', e.target.value)}
            fullWidth
            required
          />

          {/* テストの説明 */}
          <TextField
            label={t('create.testDescription', 'テストの説明（任意）')}
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            multiline
            rows={2}
            fullWidth
          />

          {/* 合格点 */}
          <TextField
            label={t('tests.passingScoreLabel', '合格点（%）')}
            type="number"
            value={formData.passingScore}
            onChange={(e) => handleChange('passingScore', parseInt(e.target.value) || 80)}
            inputProps={{ min: 0, max: 100 }}
            fullWidth
          />

          {/* 制限時間 */}
          <TextField
            label={t('tests.timeLimitLabel', '制限時間（分）')}
            type="number"
            value={formData.timeLimit}
            onChange={(e) => handleChange('timeLimit', parseInt(e.target.value) || 0)}
            inputProps={{ min: 0 }}
            fullWidth
            helperText={t('tests.timeLimitHelp', '0の場合は制限なし')}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          {t('form.cancel', 'キャンセル')}
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={saving || loading}>
          {saving ? t('form.creating', '作成中...') : t('form.create', '作成')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default TestCreateDialog
